<template>
  <Teleport to="body">
    <div
      class="media-dialog-overlay"
      @click.self="close"
    >
      <div
        class="media-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Product media viewer"
        v-focus-trap="isOpen"
        @keydown.escape="close"
      >
        <button
          class="media-dialog__close"
          @click="close"
          aria-label="Close media viewer"
        >
          <svg class="media-dialog__close-icon">
            <use xlink:href="#icon-cross-thick"></use>
          </svg>
        </button>

          <div
            ref="swiperContainer"
            :class="['media-dialog__swiper', 'swiper-container', { 'media-dialog__swiper--ready': isSwiperReady }]"
          >
          <div class="media-dialog__wrapper">
            <div
              v-for="(item, index) in gallery"
              :key="index"
              class="media-dialog__slide"
            >
              <div v-if="!isVideoItem(item)" class="media-dialog__image-container">
                <ImageMagnifier
                  class="media-dialog__image"
                  :product-image="isSlideLoaded(index) ? item.src : ''"
                  :image-index="index"
                  :aspect-ratio="isGiftCard ? '1.0352' : '0.806'"
                  :disable="false"
                  :preload-zoom="index === activeSlideIndex"
                />
              </div>

              <div
                v-else
                class="media-dialog__video"
                :style="getVideoStyles(item, index)"
              >
                <div
                  v-if="!isVideoLoaded(index)"
                  class="media-dialog__video-loader"
                  aria-hidden="true"
                >
                  <span class="media-dialog__video-spinner" />
                </div>

                <FitVideoPlayer
                  v-if="item.sources?.length"
                  :class="{ 'media-dialog__video-iframe--loading': !isVideoLoaded(index) }"
                  :sources="item.sources"
                  :poster="item.src"
                  :autoplay="index === activeSlideIndex"
                  @ready="markVideoLoaded(index)"
                  @aspect-ratio="(ratio) => onNativeAspectRatio(index, ratio)"
                />
                <iframe
                  v-else-if="item.link"
                  :class="{ 'media-dialog__video-iframe--loading': !isVideoLoaded(index) }"
                  :src="getVideoUrl(item.link)"
                  loading="lazy"
                  allow="autoplay"
                  @load="markVideoLoaded(index)"
                />
              </div>
            </div>
          </div>

          <div class="media-dialog__nav media-dialog__prev" tabindex="0" aria-label="Previous slide">
            <svg width="13" height="23" viewBox="0 0 13 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.3438 21.6562L1.03125 11.3438L11.3438 1.03125" stroke="#31A949" stroke-width="2.0625" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="media-dialog__nav media-dialog__next" tabindex="0" aria-label="Next slide">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.25 2.0625L18.5625 12.375L8.25 22.6875" stroke="#31A949" stroke-width="2.0625" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue';
import ImageMagnifier from '../../global/image-magnifier.vue';
import FitVideoPlayer from './fit-video-player.vue';
import Swiper from 'swiper';
import SwiperCore, { Navigation, A11y } from 'swiper/core';

SwiperCore.use([Navigation, A11y]);

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
  gallery: {
    type: Array,
    default: () => [],
  },
  initialIndex: {
    type: Number,
    default: 0,
  },
  isGiftCard: {
    type: Boolean,
    default: false,
  },
  isDesktopScreen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const swiperContainer = ref(null);
const swiperInstance = ref(null);
const isSwiperReady = ref(false);
const videoLoaded = ref({});
const videoAspectRatios = ref({});
const activeSlideIndex = ref(props.initialIndex);
const videoLoadTimers = {};
// Preserve any existing scroll lock (e.g. sticky bar options open) so closing
// this dialog does not unlock the page underneath.
let previousBodyOverflow = '';

// Safari re-fetches CDN images on every open (Vary: Accept defeats its cache), so
// mounting every slide's 2000px background + 4000px zoom preload at once left the
// visible image starved for seconds. Only load the active slide and its neighbors;
// slides stay loaded once visited.
const loadedSlides = ref(new Set());

const markSlideWindow = (center) => {
  const next = new Set(loadedSlides.value);
  [center - 1, center, center + 1].forEach((index) => {
    if (index >= 0 && index < props.gallery.length) {
      next.add(index);
    }
  });
  loadedSlides.value = next;
};

const isSlideLoaded = (index) => loadedSlides.value.has(index);

markSlideWindow(props.initialIndex);

// Default to landscape — fit videos are authored as wide; portrait is the exception.
const DEFAULT_VIDEO_ASPECT_RATIO = 16 / 9;

const close = () => {
  emit('close');
};

const isVideoItem = (item) => !!(item?.isVideo || item?.link || item?.sources?.length);
const isVideoLoaded = (index) => !!videoLoaded.value[index];

const markVideoLoaded = (index) => {
  videoLoaded.value = { ...videoLoaded.value, [index]: true };
  if (videoLoadTimers[index]) {
    clearTimeout(videoLoadTimers[index]);
    delete videoLoadTimers[index];
  }
};

const getPlaceholderUrl = (src) => {
  if (!src) return '';
  const url = typeof src === 'string' ? src : src.url || src.src || '';
  if (!url || typeof url !== 'string') return '';
  return url.includes('?') ? `${url}&width=1200` : `${url}?width=1200`;
};

const setVideoAspectRatio = (index, width, height) => {
  if (!width || !height) return;
  videoAspectRatios.value = {
    ...videoAspectRatios.value,
    [index]: width / height,
  };
};

const onNativeAspectRatio = (index, ratio) => {
  if (!ratio) return;
  videoAspectRatios.value = {
    ...videoAspectRatios.value,
    [index]: ratio,
  };
};

// Prefer the poster image’s natural ratio so wide vs portrait uploads both look correct.
const resolveVideoAspectRatio = (item, index) => {
  if (!isVideoItem(item) || videoAspectRatios.value[index]) return;

  if (item.aspectRatio) {
    onNativeAspectRatio(index, item.aspectRatio);
    return;
  }

  if (item.src) {
    const img = new Image();
    img.onload = () => setVideoAspectRatio(index, img.naturalWidth, img.naturalHeight);
    img.src = getPlaceholderUrl(item.src);
  }
};

const getVideoStyles = (item, index) => {
  const styles = {};
  const placeholderUrl = item.src ? getPlaceholderUrl(item.src) : '';
  if (placeholderUrl) {
    styles.backgroundImage = `url(${placeholderUrl})`;
  }
  styles['--video-aspect-ratio'] = videoAspectRatios.value[index] || item.aspectRatio || DEFAULT_VIDEO_ASPECT_RATIO;
  return styles;
};

const scheduleVideoLoadFallback = (index) => {
  if (videoLoadTimers[index]) return;
  videoLoadTimers[index] = setTimeout(() => markVideoLoaded(index), 8000);
};

const getVideoUrl = (url) => {
  const normalized = typeof url === 'string' ? url : url?.url || url?.src || '';
  if (!normalized || typeof normalized !== 'string') return '';

  const videoId = normalized.split('/').filter(Boolean).pop();
  if (!videoId) return '';

  const baseUrl = `https://player.vimeo.com/video/${videoId}`;
  const params = new URLSearchParams({
    autoplay: 1,
    loop: 1,
    muted: 1,
    api: 1,
    player_id: `modal-${videoId}`,
    controls: 1,
    title: 0,
    byline: 0,
    portrait: 0,
  });
  return `${baseUrl}?${params.toString()}`;
};

const initSwiper = () => {
  const el = swiperContainer.value;
  if (!el || swiperInstance.value) return;

  const prevEl = el.querySelector('.media-dialog__prev');
  const nextEl = el.querySelector('.media-dialog__next');

  swiperInstance.value = new Swiper(el, {
    slidesPerView: 1,
    spaceBetween: 0,
    initialSlide: props.initialIndex,
    speed: 0,
    wrapperClass: 'media-dialog__wrapper',
    slideClass: 'media-dialog__slide',
    navigation: {
      nextEl,
      prevEl,
    },
    a11y: {
      enabled: true,
      prevSlideMessage: 'Go to previous image',
      nextSlideMessage: 'Go to next image',
    },
    on: {
      slideChange() {
        activeSlideIndex.value = this.realIndex;
        markSlideWindow(this.realIndex);
      },
    },
  });

  // Force the clicked slide with no animation, then reveal to avoid a slide-0 flash.
  swiperInstance.value.slideTo(props.initialIndex, 0);
  activeSlideIndex.value = props.initialIndex;
  requestAnimationFrame(() => {
    isSwiperReady.value = true;
    // Restore normal transition speed for user navigation
    swiperInstance.value.params.speed = 300;
  });
};

onMounted(async () => {
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  props.gallery.forEach((item, index) => {
    if (isVideoItem(item)) {
      scheduleVideoLoadFallback(index);
      resolveVideoAspectRatio(item, index);
    }
  });
  await nextTick();
  requestAnimationFrame(() => {
    initSwiper();
  });
});

onUnmounted(() => {
  document.body.style.overflow = previousBodyOverflow;
  Object.keys(videoLoadTimers).forEach((key) => clearTimeout(videoLoadTimers[key]));
  if (swiperInstance.value) {
    swiperInstance.value.destroy(true, true);
    swiperInstance.value = null;
  }
});
</script>
